import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { WelcomeDiscountBannerDetail, WelcomeDiscountBannerState } from '../types'

type DiscountPerWeekProps = {
  discountDetails: WelcomeDiscountBannerDetail[]
}

type DiscountBoxStyle = {
  bgColor: Color
  textColor: Color
  showAppliedIcon: boolean
  textStyle?: React.CSSProperties
  subtitleStyle?: React.CSSProperties
}

const RoundedBox = withCitrusTheme(Box, () => ({
  borderRadius: 5,
}))

const discountBoxStyles: Record<WelcomeDiscountBannerState, DiscountBoxStyle> = {
  applied: {
    bgColor: Color.ColdGrey_500,
    textColor: Color.ColdGrey_200,
    showAppliedIcon: true,
    textStyle: { textDecoration: 'line-through' },
    subtitleStyle: { textDecoration: 'line-through', whiteSpace: 'nowrap' },
  },
  active: {
    bgColor: Color.Primary_600,
    textColor: Color.White,
    showAppliedIcon: false,
    subtitleStyle: { whiteSpace: 'nowrap' },
  },
  pending: {
    bgColor: Color.ColdGrey_500,
    textColor: Color.White,
    showAppliedIcon: false,
    subtitleStyle: { whiteSpace: 'nowrap' },
  },
}

export function DiscountPerWeek({ discountDetails }: DiscountPerWeekProps) {
  const discountBoxes = discountDetails.map((detail) => ({
    ...detail,
    style: discountBoxStyles[detail.state],
  }))

  return (
    <Box data-testid="discount-per-week" display={Display.Flex} width="100%">
      <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween} width="100%">
        {discountBoxes.map((box) => (
          <Box
            key={box.subtitle}
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.SpaceBetween}
            flexDirection={FlexDirection.Column}
            position={DisplayPosition.Relative}
            width="90px"
          >
            {box.style.showAppliedIcon && (
              <Box
                position={DisplayPosition.Absolute}
                top="-16px"
                left="50%"
                style={{ transform: 'translateX(-50%)', zIndex: 1 }}
              >
                <Box position={DisplayPosition.Relative}>
                  <Box position={DisplayPosition.Relative} style={{ zIndex: 1 }}>
                    <Icon name="success" variant={IconVariant.Confirmation} size={6} />
                  </Box>
                  <Box
                    position={DisplayPosition.Absolute}
                    top="46.5%"
                    left="51%"
                    style={{ transform: 'translate(-50%, -50%)', zIndex: 0 }}
                  >
                    <Icon name="tick" variant={IconVariant.Inherit} color={Color.White} size={5} />
                  </Box>
                </Box>
              </Box>
            )}
            <RoundedBox
              bg={box.style.bgColor}
              borderRadius={1.5}
              display={Display.Flex}
              alignItems={AlignItems.Center}
              justifyContent={JustifyContent.Center}
              flexDirection={FlexDirection.Column}
              paddingV={2}
              paddingH={[4, 2]}
              width="90px"
            >
              <Text
                size={5}
                color={box.style.textColor}
                fontWeight={FontWeight.Bold}
                textAlign={TextAlign.Center}
                style={box.style.textStyle}
              >
                {box.title.discount}
              </Text>
              <Text
                size={5}
                color={box.style.textColor}
                fontWeight={FontWeight.Bold}
                textAlign={TextAlign.Center}
                style={box.style.textStyle}
              >
                {box.title.supportingText}
              </Text>
              <Text
                size={2}
                color={box.style.textColor}
                textAlign={TextAlign.Center}
                style={box.style.subtitleStyle}
              >
                {box.subtitle}
              </Text>
            </RoundedBox>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
